import type { Codebook } from 'common/models';
import { interventionStateName } from 'helpers/orderHelpers';
import { getNow } from 'helpers/timeHelpers';
import type { TFunction } from 'i18next';
import type { InterventionState } from 'pages/interventions/models';

import type { DashboardOrderIntervention } from '../models';

interface PieDataItem {
  id: string;
  value: number;
}

const filterPeriod = (interventions: DashboardOrderIntervention[], period: 'month' | 'year') => {
  const now = getNow();

  if (period === 'month') {
    return interventions.filter((x) => now.isSame(x.created, 'month'));
  }

  return interventions.filter((x) => now.isSame(x.created, 'year'));
};

const groupByState = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<PieDataItem[]>((acc, item) => {
    const category = acc.find((x) => x.id === item.state);
    if (!category) {
      return [...acc, { id: item.state, value: 1 }];
    }

    return [...acc.filter((x) => x.id !== item.state), { ...category, value: category.value + 1 }];
  }, []);
};

const groupByCategory = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<PieDataItem[]>((acc, item) => {
    const category = acc.find((x) => x.id === item.categoryId);
    if (!category) {
      return [...acc, { id: item.categoryId, value: 1 }];
    }

    return [
      ...acc.filter((x) => x.id !== item.categoryId),
      { ...category, value: category.value + 1 }
    ];
  }, []);
};

export const prepareData = (
  interventions: DashboardOrderIntervention[],
  t: TFunction,
  selectedType: 'state' | 'category',
  selectedPeriod: 'month' | 'year',
  categories: Codebook[]
) => {
  const filtered = filterPeriod(interventions, selectedPeriod);
  let data: PieDataItem[] = [];
  if (selectedType === 'state') data = groupByState(filtered);
  if (selectedType === 'category') data = groupByCategory(filtered);

  return data.map((x) => {
    if (selectedType === 'state') {
      return { ...x, label: t(interventionStateName(x.id as InterventionState)) };
    }
    return { ...x, label: categories.find((c) => c.id === x.id)?.name || x.id };
  });
};
